import { createTheme } from '@mui/material/styles';

const BRAND = '#284B32';
const BRAND_DARK = '#1a3322';
const TEAL = '#0F6E56';
const MINT = '#C1EFD0';
const CREAM = '#f4f1e9';

const theme = createTheme({
  palette: {
    primary: {
      main: BRAND,
      dark: BRAND_DARK,
      contrastText: '#ffffff',
    },
    secondary: {
      main: TEAL,
      contrastText: '#ffffff',
    },
    background: {
      default: CREAM,
      paper: '#ffffff',
    },
    success: {
      main: '#10B981',
      light: '#d1fae5',
    },
    warning: {
      main: '#F59E0B',
      light: '#fef3c7',
    },
    error: {
      main: '#EF4444',
      light: '#fee2e2',
    },
  },

  shape: {
    borderRadius: 10,
  },

  typography: {
    fontFamily: [
      'Manrope',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      'sans-serif',
    ].join(','),
    h5: {
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 700,
    },
    subtitle1: {
      fontWeight: 600,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },

  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          paddingLeft: 16,
          paddingRight: 16,
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        contained: {
          '&:hover': {
            backgroundColor: BRAND_DARK,
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': { borderWidth: '1.5px' },
        },
        sizeSmall: {
          borderRadius: 6,
          paddingLeft: 10,
          paddingRight: 10,
        },
      },
      defaultProps: {
        disableElevation: true,
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          boxShadow: '0 1px 6px rgba(40,75,50,0.08), 0 0 0 1px rgba(40,75,50,0.07)',
          border: 'none',
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        outlined: {
          borderRadius: 12,
          border: '1px solid rgba(40,75,50,0.14)',
        },
        elevation1: {
          boxShadow: '0 1px 6px rgba(40,75,50,0.08), 0 0 0 1px rgba(40,75,50,0.07)',
          borderRadius: 12,
        },
      },
    },

    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          overflow: 'hidden',
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: BRAND,
            color: '#ffffff',
            fontWeight: 600,
            fontSize: '0.78rem',
            letterSpacing: '0.03em',
            padding: '12px 16px',
          },
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child td': { borderBottom: 0 },
          '&.MuiTableRow-hover:hover': {
            backgroundColor: 'rgba(40,75,50,0.04)',
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 600,
          fontSize: '0.72rem',
        },
        colorSuccess: {
          backgroundColor: '#d1fae5',
          color: '#065f46',
        },
        colorWarning: {
          backgroundColor: '#fef3c7',
          color: '#92400e',
        },
        colorError: {
          backgroundColor: '#fee2e2',
          color: '#991b1b',
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: BRAND,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: BRAND,
            boxShadow: `0 0 0 3px rgba(40,75,50,0.12)`,
          },
        },
        notchedOutline: {
          borderColor: '#d1d5d0',
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: BRAND,
          },
        },
      },
    },

    MuiDialog: {
      defaultProps: {
        PaperProps: {
          elevation: 8,
        },
      },
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          fontSize: '1rem',
          paddingBottom: 8,
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(40,75,50,0.12)',
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 6,
          fontSize: '0.75rem',
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
  },
});

export default theme;
